import { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Search as SearchIcon,
  CloseOutlined as CloseOutlinedIcon,
} from '@mui/icons-material';
import {
  Box,
  InputBase,
  IconButton,
  ClickAwayListener,
  Paper,
  Typography,
  CircularProgress,
  Divider,
  alpha,
  Fade,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api';
import placeholder from '../../assets/Placeholder_view.png';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '100%',
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
  },
  searchContainerMobileExpanded: {
    width: '100%',
  },
  search: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
  },
  searchMobileExpanded: {
    backgroundColor: theme.palette.background.highlight,
    borderRadius: theme.shape.borderRadius,
    transform: 'scaleX(1)',
    transformOrigin: 'center',
  },
  searchIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0, 1),
    '&:hover': {
      backgroundColor: alpha(
        theme.palette.mode === 'dark' 
          ? theme.palette.secondary.main 
          : theme.palette.secondary.light,
        0.1
      ),
    },
  },
  inputRoot: {
    flex: 1,
    alignItems: 'center',
  },
  inputInput: {
    width: '100%',
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: theme.spacing(1),
    maxHeight: '400px',
    overflowY: 'auto',
    zIndex: 1400,
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 32px rgba(0, 0, 0, 0.5)' 
      : '0 8px 32px rgba(0, 0, 0, 0.15)',
    transformOrigin: 'top center',
  },
  dropdownMobileExpanded: {
    top: 'calc(100% + 8px)',
  },
  bookItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    gap: theme.spacing(2),
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? theme.palette.background.highlight
        : '#f5f5f5',
    },
  },
  bookImage: {
    width: 50,
    height: 70,
    objectFit: 'cover',
    borderRadius: 4,
    flexShrink: 0,
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.shorter,
    }),
  },
  bookInfo: {
    flex: 1,
    minWidth: 0,
  },
  sectionHeader: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.background.highlight
      : '#fafafa',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const SearchBar = ({ onSearchClose, isMobileExpanded = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isFocused, setFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const onSearchCancel = useCallback(() => {
    setSearchTerm('');
    setFocused(false);
    setShowDropdown(false);
    setResults([]);
    onSearchClose();
  }, [onSearchClose]);

  const focusInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      setFocused(true);
    }
  }, []);

  useEffect(() => {
    if (isMobileExpanded && inputRef.current) {
      setTimeout(() => {
        focusInput();
      }, 300);
    }
  }, [isMobileExpanded, focusInput]);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Don't search if less than 2 characters
    if (searchTerm.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    // Set loading state immediately
    setLoading(true);
    setShowDropdown(true);

    // wait 0.5 second before API call
    debounceTimer.current = setTimeout(async () => {
      try {
        const data = await ApiService.instantSearch(searchTerm);
        setResults(data.books || []);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  const handleClickAway = useCallback((event) => {
    // Don't close if clicked inside the search container or dropdown
    if (
      containerRef.current &&
      containerRef.current.contains(event.target)
    ) {
      return;
    }
    
    // Don't close if there are search results (only close via close button)
    if (showDropdown && results.length > 0) {
      return;
    }
    
    // Close the search
    if (isMobileExpanded) {
      onSearchClose();
    } else {
      setShowDropdown(false);
      setFocused(false);
    }
  }, [isMobileExpanded, onSearchClose, results.length, showDropdown]);

  const handleBookClick = useCallback((book) => {
    navigate(`/book/${book.uuid}`);
    onSearchCancel();
  }, [navigate, onSearchCancel]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && (isMobileExpanded || showDropdown)) {
        onSearchCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileExpanded, onSearchCancel, showDropdown]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box 
        ref={containerRef}
        className={`${classes.searchContainer} ${isMobileExpanded ? classes.searchContainerMobileExpanded : ''}`}
      >
        <Box
          className={`${classes.search} ${isMobileExpanded ? classes.searchMobileExpanded : ''}`}
          borderRadius={theme.shape.borderRadius}
          bgcolor={theme.palette.background.highlight}
          boxShadow={isFocused || isMobileExpanded ? 1 : 0}
          height={'3rem'}
          sx={{
            transform: isMobileExpanded ? 'scaleX(1)' : 'none',
            transformOrigin: 'center',
          }}
        >
          <div 
            className={classes.searchIconContainer}
            onClick={focusInput}
          >
            <SearchIcon />
          </div>
          <InputBase
            placeholder="বই, লেখক, বা প্রকাশক খুঁজুন"
            inputRef={inputRef}
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            value={searchTerm}
            onClick={() => setFocused(true)}
            inputProps={{ 'aria-label': 'search' }}
            onChange={(event) => setSearchTerm(event.target.value)}
            sx={{
              opacity: isMobileExpanded ? 1 : (isFocused || searchTerm ? 1 : 0.7),
              transition: theme.transitions.create('opacity', {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          />
          {(isFocused || isMobileExpanded || searchTerm) ? (
            <Fade in={true}>
              <IconButton 
                onClick={onSearchCancel}
                size="small"
                sx={{
                  marginRight: 1,
                  transition: theme.transitions.create('opacity', {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              >
                <CloseOutlinedIcon 
                  fontSize="small" 
                  htmlColor={theme.custom.palette.iconColor} 
                />
              </IconButton>
            </Fade>
          ) : null}
        </Box>

        <Collapse in={showDropdown && searchTerm.length >= 2}>
          <Fade in={showDropdown && searchTerm.length >= 2}>
            <Paper className={`${classes.dropdown} ${isMobileExpanded ? classes.dropdownMobileExpanded : ''}`}>
              {loading ? (
                <Box className={classes.loadingContainer}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <>
                  <Box>
                    <Box className={classes.sectionHeader}>
                      <Typography variant="subtitle2" fontWeight={600} color="textPrimary">
                        বই ({results.length} টি)
                      </Typography>
                    </Box>
                    
                    {results.length === 0 ? (
                      <Box className={classes.emptyState}>
                        <Typography variant="body2">
                          কোনো বই পাওয়া যায়নি
                        </Typography>
                      </Box>
                    ) : (
                      results.map((book, index) => (
                        <Fade in={true} key={book.uuid} timeout={index * 50}>
                          <div>
                            <Box
                              className={classes.bookItem}
                              onClick={() => handleBookClick(book)}
                            >
                              <img
                                src={book.image || placeholder}
                                alt={book.name}
                                className={classes.bookImage}
                                onError={(e) => {
                                  e.target.src = placeholder;
                                }}
                              />
                              <Box className={classes.bookInfo}>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color="textPrimary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {book.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {book.writer}
                                </Typography>
                                <Typography variant="body2" color="secondary.main" fontWeight={600}>
                                  ৳{book.discounted_price.toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                            {index < results.length - 1 && <Divider />}
                          </div>
                        </Fade>
                      ))
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </Fade>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchBar;
